import React, { useState } from 'react'
import {
  Accordion,
  AnimatePresence,
  YStack,
  ScrollView,
  XStack,
  useMedia,
  Separator,
  Text,
  Icon,
  Spinner,
  styled,
} from '@red-ui/components'
import { Header } from './Header'
import { SeatTypeCard } from './SeatTypeCard'
import { CABIN_DETAILS_QUERY_KEY } from './SeatTypeInfo'
import { getTrackingId } from '../../helpers/getTrackingId'
import { formatPoints } from '@vrw/data/src/utils/formatters'
import { getString } from '@vrw/data'
import { CURRENCY_SYMBOLS, FlightAndFares } from '../../api/searchFlights'
import { useIsFetching } from '@tanstack/react-query'

export type Props = {
  value: string
  isActive: boolean
  onSelect: (fareId: string) => void
  data: FlightAndFares
}

const PriceText = styled(Text, {
  fontSize: '$4',
  $gtMobile: { fontSize: '$6' },
  $gtTabletLandscape: { fontSize: '$7' },
})

export const FlightDataCard = ({ value, isActive, onSelect, data }: Props) => {
  const { gtTabletPortrait } = useMedia()
  const [detailsOpened, setDetailsOpened] = useState(false)
  const activeCabinDetailsRequests = useIsFetching({ queryKey: [CABIN_DETAILS_QUERY_KEY] })
  const isCabinDetailsLoading = activeCabinDetailsRequests > 0

  const seatTypeCards = data?.fares?.map((fare, index) => (
    <SeatTypeCard
      key={`${fare.fareFamilyType}_${index}`}
      {...fare}
      onSelect={() => fare.id && onSelect(fare.id)}
      expanded={detailsOpened}
      onExpand={setDetailsOpened}
    />
  ))

  return (
    <Accordion.Item
      value={value}
      backgroundColor="$white"
      borderWidth={1}
      borderRadius="$4"
      borderColor={isActive ? '$black10' : '$black2'}>
      {/* ui lib auto makes a h1 tag for the accordion header, so we need to use a div tag */}
      <Accordion.Header tag="div" width="100%">
        <Accordion.Trigger
          id={getTrackingId(getString('redSkySearch.results.card.ariaLabel'))}
          borderRadius="$4"
          cursor="pointer"
          backgroundColor="$white"
          unstyled
          width="100%"
          aria-label={getString('redSkySearch.results.card.ariaLabel')}
          hoverStyle={{ backgroundColor: '$white' }}
          focusStyle={{ backgroundColor: '$white' }}>
          <YStack padding="$2" $gtMobile={{ padding: '$3' }} width="100%">
            <Header data={data.flight} />
            {!isActive && (
              <>
                <Separator marginVertical="$2" $gtMobile={{ marginVertical: '$3' }} />
                <XStack gap="$2" overflow="scroll">
                  {data?.fares.map((fare, index) => {
                    const isAvailable = fare.availableSeatCount != null
                    const isLowAvailability = !!fare.availableSeatCount && fare.availableSeatCount < 9
                    const { awardPoints, currency, amountIncludingTax } = fare.price ?? {}

                    let cabinName = getString(`redSkySearch.results.card.fare.family.${fare.fareFamilyType.toLowerCase()}`)

                    // direct flights have always only one fare segment
                    if (fare?.fareSegments !== null && fare?.fareSegments?.length > 0) cabinName = fare.fareSegments[0].cabinName

                    return (
                      <XStack
                        key={`${fare.fareFamilyType}_${index}`}
                        borderWidth={1}
                        backgroundColor={isAvailable ? '$pink5' : '$white'}
                        borderColor={isAvailable ? '$transparent' : '$black2'}
                        padding="$2"
                        flexGrow={1}
                        flexBasis="0%"
                        justifyContent="space-between"
                        borderRadius="$4"
                        alignItems="center"
                        minWidth={284}>
                        <YStack alignItems="flex-start" gap="$1">
                          {isLowAvailability && (
                            <Text
                              fontSize="$3"
                              $gtMobile={{ fontSize: '$4' }}
                              color="$white"
                              backgroundColor="$red6"
                              borderRadius="$4"
                              paddingHorizontal="$1"
                              paddingVertical="$0.25">
                              {getString('redSkySearch.results.card.fare.left').replace(
                                '{{availableSeatCount}}',
                                `${fare.availableSeatCount}`
                              )}
                            </Text>
                          )}
                          <Text fontSize="$4" $gtMobile={{ fontSize: '$5' }} $gtTabletLandscape={{ fontSize: '$6' }}>
                            {cabinName}
                          </Text>
                          {isAvailable ? (
                            <XStack alignItems="center" gap="$1">
                              <PriceText fontWeight="600">{`${formatPoints({ points: Number(awardPoints) })} Pts`}</PriceText>
                              {currency && amountIncludingTax && (
                                <>
                                  <PriceText>+</PriceText>
                                  <PriceText>{`${CURRENCY_SYMBOLS[currency]}${amountIncludingTax.toFixed(2)}`}</PriceText>
                                </>
                              )}
                            </XStack>
                          ) : (
                            <Text fontSize="$3" $gtMobile={{ fontSize: '$4' }}>
                              {getString('redSkySearch.results.card.fare.unavailable')}
                            </Text>
                          )}
                        </YStack>
                        {isAvailable && <Icon.ChevronDown color="$black" size="$2" />}
                      </XStack>
                    )
                  })}
                </XStack>
              </>
            )}
          </YStack>
        </Accordion.Trigger>
      </Accordion.Header>
      <AnimatePresence>
        <Accordion.Content borderRadius="$4" unstyled exitStyle={{ opacity: 0 }} backgroundColor="$white">
          {isCabinDetailsLoading && <Spinner color="$red6" size="large" marginBottom="$4" marginTop="$2" />}
          {gtTabletPortrait ? (
            <XStack display={isCabinDetailsLoading ? 'none' : 'inherit'} paddingLeft="$3" marginBottom="$3">
              {seatTypeCards}
            </XStack>
          ) : (
            <ScrollView
              display={isCabinDetailsLoading ? 'none' : 'inline'}
              horizontal
              paddingLeft="$2"
              marginBottom="$2"
              $gtMobile={{ paddingLeft: '$3', marginBottom: '$3' }}>
              {seatTypeCards}
            </ScrollView>
          )}
        </Accordion.Content>
      </AnimatePresence>
    </Accordion.Item>
  )
}
